import { useMemo } from 'react';

import { getPaintedIconCount } from '../ssr/bylinePlaceholderIconCount.ts';
import { useOutboundBylineSsrState } from '../ssr/useOutboundBylineSsrState.ts';
import { useIncomingLinksSsrState } from '../ssr/useIncomingLinksSsrState.ts';
import {
	isBylineConnectionIconStackFinal,
	MAX_BYLINE_CONNECTION_ICONS,
} from '../utils/getBylineConnectionIcons.ts';
import { getBylineVisibility, type BylineVisibility } from '../utils/getBylineVisibility.ts';
import { getDevDocsConnectionIcons } from '../utils/getDevDocsConnectionIcons.ts';
import type { BylineContentType } from '../utils/contentAri.ts';

import type { useBylineConnectionsData } from './useBylineConnectionsData.ts';
import type { BylineTileReservation } from './useBylineTileReservation.ts';

type BylineConnectionsData = ReturnType<typeof useBylineConnectionsData>;

type BylineConnectionsVisibilityOptions = {
	contentId: string;
	contentType: BylineContentType;
	isEmbeddedMenu: boolean;
	/** The server's tile count and SSR load flag from `useBylineTileReservation`, above Suspense. */
	reservation: BylineTileReservation;
	data: {
		outgoing: Pick<BylineConnectionsData['outgoing'], 'links' | 'loading'>;
		incoming: Pick<BylineConnectionsData['incoming'], 'twgLinks' | 'loading'>;
		relatedContent: Pick<BylineConnectionsData['relatedContent'], 'links' | 'loading'>;
		devDocsConnection: BylineConnectionsData['devDocsConnection'];
	};
};

type BylineConnectionsVisibility = BylineVisibility & {
	hiddenBySsr: boolean;
	devDocsIcons: ReturnType<typeof getDevDocsConnectionIcons>;
	/** Icon tiles the server painted for this page; zero after a client-side navigation. */
	reservedIconCount: number;
	/** Whether this page view started as a hydrated server render; false when rendered fresh. */
	isSsrLoad: boolean;
	/** Tiles the loaded stack must keep; `undefined` when nothing was painted, so it is not capped. */
	paintedIconCount: number | undefined;
};

/** Combines loaded visibility rules with the server's retained hide decision. */
export const useBylineConnectionsVisibility = ({
	contentId,
	contentType,
	isEmbeddedMenu,
	reservation,
	data,
}: BylineConnectionsVisibilityOptions): BylineConnectionsVisibility => {
	const { outgoing, incoming, relatedContent, devDocsConnection } = data;
	const { links: outgoingLinks, loading: outgoingLinksLoading } = outgoing;
	const { twgLinks: twgIncomingLinks, loading: incomingLinksLoading } = incoming;
	const { links: relatedContentLinks, loading: relatedContentLoading } = relatedContent;
	const devDocsIcons = useMemo(
		() => getDevDocsConnectionIcons(devDocsConnection),
		[devDocsConnection],
	);
	const outboundState = useOutboundBylineSsrState(contentId);
	const incomingState = useIncomingLinksSsrState(contentId, contentType);
	const { isSsrLoad, reservedIconCount } = reservation;
	// On a hydrated load the server paints no tiles exactly when it hides the byline. That decision
	// holds until navigation, even if client queries later find more links.
	const hiddenBySsr = isSsrLoad && reservedIconCount === 0;
	const paintedIconCount = getPaintedIconCount(reservedIconCount);
	// When the server painted tiles, the stack has exactly that many slots, so it is final as soon as
	// the sources ahead of the first pending one fill those; the fill order is unchanged. After a
	// client-side navigation nothing was painted, so the stack is the full size.
	const iconCapacity = paintedIconCount ?? MAX_BYLINE_CONNECTION_ICONS;
	const isIconStackFinal = useMemo(
		() =>
			isBylineConnectionIconStackFinal(
				{
					additionalIcons: devDocsIcons,
					relatedContentLinks,
					incomingLinks: twgIncomingLinks,
					outgoingLinks,
				},
				{ relatedContentLoading, incomingLinksLoading, outgoingLinksLoading },
				iconCapacity,
			),
		[
			devDocsIcons,
			relatedContentLinks,
			twgIncomingLinks,
			outgoingLinks,
			relatedContentLoading,
			incomingLinksLoading,
			outgoingLinksLoading,
			iconCapacity,
		],
	);
	// The embedded byline's SSR hold uses the samples' presence, as it did before the placeholder
	// counted apps (CCINT-9543 left that rule alone): an outbound or incoming sample with rows, or
	// Dev Docs. Whether that hold should survive below the row minimum is a separate decision.
	const hasSsrConnectionSignal =
		outboundState?.status === 'present' ||
		incomingState?.status === 'present' ||
		devDocsConnection !== null;
	// The show/hide and wait rules for both menus live in `getBylineVisibility`.
	const { hasConnection, isResolving, shouldRenderByline } = useMemo(
		() =>
			getBylineVisibility({
				isEmbeddedMenu,
				outgoingLinks,
				incomingLinks: twgIncomingLinks,
				relatedContentLinks,
				hasDevDocsConnection: devDocsConnection !== null,
				outgoingLinksLoading,
				relatedContentLoading,
				isIconStackFinal,
				hasSsrConnectionSignal,
				hasServerTiles: paintedIconCount !== undefined,
			}),
		[
			isEmbeddedMenu,
			outgoingLinks,
			twgIncomingLinks,
			relatedContentLinks,
			devDocsConnection,
			outgoingLinksLoading,
			relatedContentLoading,
			isIconStackFinal,
			hasSsrConnectionSignal,
			paintedIconCount,
		],
	);

	return {
		hiddenBySsr,
		hasConnection,
		isResolving,
		shouldRenderByline,
		devDocsIcons,
		reservedIconCount,
		isSsrLoad,
		paintedIconCount,
	};
};
